import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from 'src/app/main/backoffice/shared/services/snackbar.service';
import { ParametrosService } from '../../../parametros/services/parametros.service';
import { Producto } from '../../../productos/models/producto.model';
import { ProductosService } from '../../../productos/services/productos.service';
import { Topping } from '../../models/crearPedido.dto';
import { MetodoPago, Pedido, PedidoItem } from '../../models/pedido.model';
import { PedidosService } from '../../services/pedidos.service';

@Component({
  selector: 'app-editar-pedido-dialog',
  templateUrl: './editar-pedido-dialog.component.html',
  styleUrls: ['./editar-pedido-dialog.component.scss'],
})
export class EditarPedidoDialogComponent implements OnInit {
  form!: FormGroup;
  metodoPago = MetodoPago;
  productosDisponibles: Producto[] = [];
  pedidoId!: number;
  tieneDelivery: boolean = false;
  public toppingsDisponibles: Topping[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public pedido: Pedido,
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<EditarPedidoDialogComponent>,
    private pedidosService: PedidosService,
    private productosService: ProductosService,
    private snackbarService: SnackbarService,
    private parametrosService: ParametrosService,
  ) {
    this.pedidoId = pedido.id;
  }

  ngOnInit(): void {
    this.buildForm();
    this.cargarProductos(); //Obtiene los productos disponibles para agregarlos al pedido
    this.obtenerToppings();
    this.obtenerParametros();
    this.cargarPedido(); //Obtiene los detalles del pedido es un obtener por ID
  }

  obtenerToppings(): void {
    this.productosService.obtenerToppings().subscribe((res) => {
      this.toppingsDisponibles = res;
    });
  }

  esCategoriaSinToppings(productId: number): boolean {
    return (
      this.productosDisponibles.find((p) => p.id === productId)?.categoryId ===
      3
    );
  }

  private obtenerParametros(): void {
    this.parametrosService.obtenerParametros().subscribe((params) => {
      this.tieneDelivery =
        params.find((p) => p.key === 'delivery_enabled')?.value === 'true';
    });
  }

  private cargarPedido(): void {
    this.pedidosService.obtenerDetallePedido(this.pedido.id).subscribe({
      next: (pedido) => {
        this.pedido = pedido;
        this.patchForm();
      },
      error: () => {
        this.snackbarService.error(
          'Error',
          'Ha ocurrido un error al cargar los detalles del pedido',
          7000,
        );
        this.dialogRef.close();
      },
    });
  }

  private cargarProductos(): void {
    this.productosService
      .obtenerProductos({ isAvailable: true })
      .subscribe((productos) => {
        this.productosDisponibles = productos;
      });
  }

  agregarProducto(producto: Producto): void {
    if (!producto) return;

    this.items.push(
      this.fb.group({
        productId: [producto.id, Validators.required],
        quantity: [1, [Validators.required, Validators.min(1)]],
        notes: [''],
        toppings: [[]],
      }),
    );
  }

  obtenerNombreProducto(productId: number): string {
    return (
      this.productosDisponibles.find((p) => p.id === productId)?.name ||
      'Producto'
    );
  }

  eliminarItem(index: number): void {
    this.items.removeAt(index);
  }

  private buildForm(): void {
    this.form = this.fb.group({
      deliveryType: [null, Validators.required],
      deliveryAddress: [''],
      deliveryNotes: [''],
      items: this.fb.array([]),
    });
  }

  private patchForm(): void {
    if (!this.pedido) return;

    this.form.patchValue({
      deliveryType: this.pedido.deliveryType,
      deliveryAddress: this.pedido.deliveryAddress,
      deliveryNotes: this.pedido.deliveryNotes,
    });

    this.items.clear();

    this.pedido.items.forEach((item) => {
      this.items.push(this.crearItemForm(item));
    });
  }

  private crearItemForm(item: PedidoItem): FormGroup {
    return this.fb.group({
      productId: [item.productId, Validators.required],
      quantity: [item.quantity ?? 1, [Validators.required, Validators.min(1)]],
      notes: [item.notes],
      toppings: [
        item.toppings?.map((t) => t.toppingId) || [], // 👈 SOLO IDS
      ],
    });
  }

  public get items(): FormArray {
    return this.form.get('items') as FormArray;
  }

  public guardar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const itemsExpandidos = this.form.value.items.flatMap((item: any) => {
      const toppings = (item.toppings || []).map((toppingId: number) => ({
        toppingId,
        quantity: 1,
      }));

      return Array.from({ length: item.quantity }).map(() => ({
        productId: item.productId,
        notes: item.notes,
        toppings,
      }));
    });

    const payload = {
      deliveryType: this.form.value.deliveryType,
      deliveryAddress: this.form.value.deliveryAddress,
      deliveryNotes: this.form.value.deliveryNotes,
      items: itemsExpandidos,
    };

    this.pedidosService.actualizarPedido(this.pedidoId, payload).subscribe({
      next: () => {
        this.snackbarService.success(
          'Éxito',
          'Pedido actualizado correctamente',
          7000,
        );
        this.dialogRef.close(true);
      },
      error: () => {
        this.snackbarService.error(
          'Error',
          'Ha ocurrido un error al actualizar el pedido',
          7000,
        );
      },
    });
  }

  public cancelar(): void {
    this.dialogRef.close();
  }

  obtenerNombreTopping(toppingId: number): string {
    return (
      this.toppingsDisponibles.find((t) => t.id === toppingId)?.name ??
      'Topping'
    );
  }
}
